import { useQuery } from '@tanstack/react-query';
import i18next from 'i18next';
import { lazy, ReactNode, Suspense } from 'react';

import {
	getFooter,
	getMainNav,
	getPartnerLogos,
} from 'services/mancity-template';

import { useBrowsing } from 'hooks/use-browsing';

import {
	PUBLIC_FF_HEADER_BASKET_ICON,
	PUBLIC_GTM_ID_ENV,
} from 'tools/environments';

const ManCityTemplate = lazy(() => import('ui/mancity/template'));

type LayoutProps = {
	children: ReactNode;
};

const analyticsConfig = {
	gtmId: PUBLIC_GTM_ID_ENV,
};

export const Layout = ({ children }: LayoutProps) => {
	const { query } = useBrowsing();
	const isFromApp = query.fromapp !== undefined;
	const locale = i18next.language || 'en';

	const partnerLogosQuery = useQuery({
		queryKey: ['partners', locale],
		queryFn: () =>
			getPartnerLogos(locale).then((response) => response.data),
		enabled: !isFromApp,
	});

	const footerQuery = useQuery({
		queryKey: ['footer', locale],
		queryFn: () => getFooter(locale).then((response) => response.data),
		enabled: !isFromApp,
	});

	const mainNavQuery = useQuery({
		queryKey: ['main-nav', locale],
		queryFn: () => getMainNav(locale).then((response) => response.data),
		enabled: !isFromApp,
	});

	const navItems =
		mainNavQuery?.data?.find(({ navgroup }) => navgroup === 'primary')
			?.items || [];

	return (
		<Suspense fallback={null}>
			<ManCityTemplate
				headerNavigationData={navItems}
				partnersData={partnerLogosQuery.data}
				footerData={footerQuery.data}
				config={analyticsConfig}
				showBasketIcon={PUBLIC_FF_HEADER_BASKET_ICON}
			>
				{children}
			</ManCityTemplate>
		</Suspense>
	);
};
